// components/ui/inputs/SearchInput.tsx
'use client';
import React, { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { getActiveLocale } from '@/utils/active-locale';
import SearchButton from '../buttons/SearchButton';

interface SearchInputProps {
  translations: { home: { right_fit: string } };
  clear?: boolean;
  label?: boolean;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  translations,
  clear,
  label,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = getActiveLocale();

  // Seed initial term from the URL
  const initial = searchParams.get('search') ?? '';
  const [term, setTerm] = useState(initial);

  const runSearch = (value: string) => {
    const t = value.trim();
    const base = `/${locale}/voor-talenten/vacatures`;
    router.push(t ? `${base}?search=${encodeURIComponent(t)}` : base);
  };

  const onSearch = () => {
    runSearch(term);
  };

  const onClear = () => {
    setTerm('');
    runSearch('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runSearch(term);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTerm(e.target.value);
  };

  return (
    <div className="flex w-full items-center justify-center">
      <div className="w-full max-w-xl">
        {label && (
          <label
            htmlFor="find-vacancy"
            className="block text-body-md font-medium text-white"
          >
            {translations.home.right_fit}
          </label>
        )}
        <div className="flex mt-2 gap-x-2">
          <input
            id="find-vacancy"
            name="find-vacancy"
            type="text"
            value={term}
            onKeyDown={onKeyDown}
            onChange={onChange}
            placeholder="Software Engineer, Java, etc."
            className="flex-1 rounded-full bg-dark-900 px-6 py-1.5 text-base text-white outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-brand-600"
          />
          <SearchButton onClick={onSearch} />
        </div>
        {clear && initial.length > 0 && (
          <button
            className="ml-6 mt-4 self-end text-gray-400 hover:text-gray-300"
            onClick={onClear}
          >
            <span className="text-xl pr-3">×</span>{locale === 'nl' ? 'Wis zoekopdracht' : 'Clear search'}
          </button>
        )}
      </div>
    </div>
  );
};